import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Clock3, Loader2, Pencil, Quote, RefreshCcw, Sparkles, Trash2 } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { EmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import {
  createTestimonial,
  deleteTestimonial,
  getTestimonials,
  Testimonial,
  TestimonialPayload,
  updateTestimonial
} from '../lib/api/testimonials'

const initialForm: TestimonialPayload = { title: '', description: '' }

const formatDate = (value?: string | null) => {
  if (!value) {
    return 'Recently added'
  }
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(new Date(value))
  } catch (error) {
    console.warn('Failed to format date', error)
    return value
  }
}

export const TestimonialsAdminPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [form, setForm] = useState<TestimonialPayload>(initialForm)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const role = (user?.role ?? 'Guest').toString()
  const isEditor = role === 'Admin' || role === 'Editor'

  const loadTestimonials = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getTestimonials()
      setTestimonials(data)
    } catch (error) {
      console.error('Failed to load testimonials', error)
      addToast({
        title: 'Could not load testimonials',
        description: 'Please refresh or try again later.'
      })
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    if (!isEditor) {
      setLoading(false)
      return
    }
    void loadTestimonials()
  }, [isEditor, loadTestimonials])

  const sortedTestimonials = useMemo(
    () =>
      [...testimonials].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [testimonials]
  )

  const resetForm = () => {
    setForm(initialForm)
    setEditingId(null)
    setFormError(null)
  }

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const trimmedTitle = form.title.trim()
      const trimmedDescription = form.description.trim()

      if (!trimmedTitle || !trimmedDescription) {
        setFormError('Both title and description are required.')
        return
      }

      setFormError(null)
      setSaving(true)

      try {
        if (editingId) {
          const updated = await updateTestimonial(editingId, {
            title: trimmedTitle,
            description: trimmedDescription
          })
          setTestimonials((prev) =>
            prev.map((item) => (item.id === editingId ? updated : item))
          )
          addToast({ title: 'Testimonial updated', description: 'Changes saved to the cache and API.' })
        } else {
          const created = await createTestimonial({ title: trimmedTitle, description: trimmedDescription })
          setTestimonials((prev) => [created, ...prev])
          addToast({ title: 'Testimonial added', description: 'Cached for instant delivery to the app.' })
        }
        resetForm()
      } catch (error) {
        console.error('Failed to save testimonial', error)
        addToast({
          title: 'Save failed',
          description: 'Could not save this testimonial. Please try again.'
        })
      } finally {
        setSaving(false)
      }
    },
    [editingId, form.description, form.title, addToast]
  )

  const handleEdit = (testimonial: Testimonial) => {
    setEditingId(testimonial.id)
    setForm({
      title: testimonial.title,
      description: testimonial.description
    })
    setFormError(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (id: string) => {
    const target = testimonials.find((item) => item.id === id)
    if (!target) {
      return
    }

    const confirmed = window.confirm(`Delete the testimonial titled "${target.title}"?`)
    if (!confirmed) {
      return
    }

    setDeletingId(id)
    try {
      await deleteTestimonial(id)
      setTestimonials((prev) => prev.filter((item) => item.id !== id))
      addToast({ title: 'Testimonial removed', description: 'It has been evicted from the cache.' })
      if (editingId === id) {
        resetForm()
      }
    } catch (error) {
      console.error('Failed to delete testimonial', error)
      addToast({ title: 'Delete failed', description: 'Unable to delete this testimonial right now.' })
    } finally {
      setDeletingId(null)
    }
  }

  if (!isEditor) {
    return (
      <Card className="border-dashed border-amber-500/40 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            Limited Access
          </CardTitle>
          <CardDescription>
            Testimonials can only be managed by Admin or Editor accounts. Contact an admin if you need access.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-3">
        <div className="xl:col-span-1 space-y-4">
          <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100 shadow-xl">
            <CardHeader className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-emerald-500/20 p-3 ring-1 ring-emerald-500/30">
                  <Sparkles className="h-6 w-6 text-emerald-200" />
                </div>
                <div>
                  <CardTitle className="text-lg">Spotlight Testimonials</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Cached at API startup and served instantly to the mobile carousel.
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-emerald-200/80 font-semibold">
                <CheckCircle2 className="h-4 w-4" />
                Cached on boot
                <Clock3 className="h-4 w-4" />
                Zero extra lookups
              </div>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Use concise titles and friendly, first-person descriptions. These will rotate in the app to promote the experience.
            </CardContent>
          </Card>

          <Card className="shadow-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Quote className="h-5 w-5 text-primary" />
                    {editingId ? 'Edit testimonial' : 'Add a testimonial'}
                  </CardTitle>
                  <CardDescription>
                    Titles should be short. Descriptions can be a few sentences of social proof.
                  </CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={loadTestimonials} title="Reload testimonials">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground" htmlFor="testimonial-title">
                    Title
                  </label>
                  <Input
                    id="testimonial-title"
                    value={form.title}
                    onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                    placeholder="Example: “Transformative training before Everest Base Camp”"
                    disabled={saving}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground" htmlFor="testimonial-description">
                    Description
                  </label>
                  <Textarea
                    id="testimonial-description"
                    value={form.description}
                    onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                    rows={5}
                    placeholder="Share a 2–4 sentence quote that highlights the app impact."
                    disabled={saving}
                  />
                </div>

                {formError ? (
                  <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    {formError}
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center gap-3">
                  <Button type="submit" disabled={saving} className="gap-2">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    {editingId ? 'Save changes' : 'Publish testimonial'}
                  </Button>
                  {editingId ? (
                    <Button type="button" variant="outline" onClick={resetForm} disabled={saving}>
                      Cancel edit
                    </Button>
                  ) : null}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="xl:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Live testimonials</h2>
              <p className="text-sm text-muted-foreground">
                {sortedTestimonials.length} item{sortedTestimonials.length === 1 ? '' : 's'} cached and ready for the app carousel.
              </p>
            </div>
          </div>

          {loading ? (
            <Card className="flex items-center gap-3 px-6 py-5">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading testimonials from cache…</p>
            </Card>
          ) : sortedTestimonials.length === 0 ? (
            <EmptyState
              icon={Quote}
              title="No testimonials yet"
              description="Add the first spotlight story to showcase how HikeStrong helps real people."
            >
              <Button onClick={() => document.getElementById('testimonial-title')?.focus()} className="gap-2">
                <Pencil className="h-4 w-4" />
                Start writing
              </Button>
            </EmptyState>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {sortedTestimonials.map((testimonial) => (
                <Card key={testimonial.id} className="flex flex-col justify-between shadow-sm">
                  <CardHeader className="space-y-2">
                    <CardTitle className="text-lg flex items-start gap-2">
                      <Quote className="mt-1 h-4 w-4 text-primary" />
                      <span>{testimonial.title}</span>
                    </CardTitle>
                    <CardDescription className="text-sm leading-relaxed text-foreground">
                      {testimonial.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <Clock3 className="h-4 w-4" />
                      {formatDate(testimonial.createdAt)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(testimonial)}
                        disabled={saving || deletingId === testimonial.id}
                        className="gap-1"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => void handleDelete(testimonial.id)}
                        disabled={deletingId === testimonial.id || saving}
                      >
                        {deletingId === testimonial.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
